import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Tag, Sparkles } from 'lucide-react';
import { PROJECTS } from '../data';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find(p => p.id === parseInt(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <section className="min-h-screen py-24 bg-black pt-32 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-serif text-white mb-4">Project Not Found</h2>
          <p className="text-gray-400 mb-8">The project you're looking for doesn't exist.</p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium hover:shadow-lg hover:shadow-yellow-500/30 transition"
          >
            <ArrowLeft size={18} /> Back to Portfolio
          </Link>
        </div>
      </section>
    );
  }

  const categoryMap = {
    graphic: 'Graphic Design',
    video: 'Videography',
    photo: 'Photography',
    motion: 'Motion Graphics',
  };

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden"
    >
      {/* Background decorations – now yellow */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back button */}
        <motion.div variants={fadeUp} className="mb-8">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Portfolio
          </Link>
        </motion.div>

        {/* Project Hero – gold theme */}
        <motion.div variants={fadeUp} className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-yellow-500/10 mb-10">
          <div className="relative bg-gradient-to-r from-yellow-900/20 to-yellow-900/20">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 md:p-10">
              <span className="inline-block px-3 py-1 rounded-full bg-yellow-500/30 text-yellow-200 text-xs font-medium border border-yellow-500/30 backdrop-blur-sm mb-3">
                {categoryMap[project.category] || project.category}
              </span>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white">{project.title}</h1>
            </div>
          </div>
        </motion.div>

        {/* Project Details Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          <motion.div variants={fadeUp} className="md:col-span-2">
            <h2 className="text-xl font-semibold text-white mb-4">About This Project</h2>
            <p className="text-gray-300 leading-relaxed mb-6">{project.description}</p>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300"
                >
                  <Tag size={14} className="inline mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Project Info</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Category</span>
                  <p className="text-white font-medium">{categoryMap[project.category] || project.category}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Project ID</span>
                  <p className="text-white font-medium">#{project.id}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Tags</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {project.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/20">
                        {tag}
                      </span>
                    ))}
                    {project.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{project.tags.length - 3} more</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Link
              to="/contact"
              className="block text-center px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium hover:shadow-lg hover:shadow-yellow-500/30 transition"
            >
              Enquire About This Project
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default ProjectDetails;